import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useApi } from 'src/hooks/useApi';
import { TopicChangesApiType, TopicDiffApiType } from 'src/types/useApi';
import { TopicChanges } from './types';

/**
 * Local snapshot of the response for a specific `(documentId, lcmsXmlGUID)`
 * key. Captured off `makeRequest`'s return value / rejection so a late-
 * resolving previous request cannot overwrite the current key's data via
 * the shared `useApi.data` state.
 */
type ResolvedTopicChanges = {
    key: string;
    data?: TopicChanges;
    error?: Error | AxiosError;
};

/**
 * Fetch change metadata for a single topic within a document.
 *
 * Wraps the shared `useApi` primitive against
 * `GET topic-changes/{documentId}/{lcmsXmlGUID}`. Fires on mount whenever
 * both identifiers are non-empty.
 *
 * `isNotTracked` is the OR of two "no diff history recorded yet" signals
 * so callers (badge + panel) can hide themselves on the empty-state
 * branch regardless of which shape the service returned (Requirements
 * 3.2, 3.10):
 *
 *   1. HTTP 404 — the legacy service shape, still in play while the
 *      service deploy is in flight and old lambdas keep 404ing for
 *      topics with only one S3 version.
 *   2. HTTP 200 with `latestChangeAt == null` — the new service shape;
 *      the no-record case is returned as an all-empty 200 (plus a
 *      `TopicChangesNoRecord` server-side metric) rather than a 404
 *      so the 4xx alarm doesn't fire on legitimate single-version
 *      topics.
 *
 * `is404` is still exported so the raw signal remains available for
 * tests and backward compatibility during the deploy window, but new
 * consumers should prefer `isNotTracked`.
 */
export const useTopicChanges = (
    documentId: string,
    lcmsXmlGUID: string
): Omit<TopicChangesApiType, 'data' | 'error'> & {
    data: TopicChanges | undefined;
    error: Error | AxiosError | undefined;
    is404: boolean;
    isNotTracked: boolean;
} => {
    // `throwOnError: true` lets the useEffect below branch on the
    // Promise's resolve vs reject path to tell success from failure for
    // THIS specific request, independent of the shared `useApi.data` /
    // `useApi.error` state which can be overwritten by a late-arriving
    // previous request.
    const api = useApi({ method: 'GET' }, true) as TopicChangesApiType;
    const { makeRequest } = api;

    // The resolved snapshot is keyed on `(documentId, lcmsXmlGUID)` and
    // only read when its key matches the currently-requested key, so a
    // stale previous-topic response can never leak across navigation —
    // even if that stale response's `setData` call lands in the shared
    // `useApi.data` state after a successful current-topic fetch has
    // already flipped `resolved.key` to the new topic.
    const requestedKey =
        documentId && lcmsXmlGUID ? `${documentId}:${lcmsXmlGUID}` : undefined;
    const [resolved, setResolved] = useState<
        ResolvedTopicChanges | undefined
    >();

    useEffect(() => {
        if (!documentId || !lcmsXmlGUID) return;
        const key = `${documentId}:${lcmsXmlGUID}`;
        let cancelled = false;
        // Clear while fetching so consumers see loading=true and
        // data=undefined, not the previous topic's payload.
        setResolved(undefined);
        // Wrap in Promise.resolve/.then/.catch so test mocks that return
        // a bare value (jest.fn() returning undefined) are handled the
        // same as the real async makeRequest. The cancellation guards
        // ensure a late-arriving response from a prior navigation can
        // neither flip `resolved` back to an older key nor overwrite
        // the current key's snapshot.
        Promise.resolve(
            makeRequest({
                url: `topic-changes/${documentId}/${lcmsXmlGUID}`,
            })
        )
            .then((response) => {
                if (cancelled) return;
                setResolved({
                    key,
                    data: response?.data as TopicChanges | undefined,
                    error: undefined,
                });
            })
            .catch((err: Error | AxiosError) => {
                if (cancelled) return;
                setResolved({ key, data: undefined, error: err });
            });
        return () => {
            cancelled = true;
        };
        // Intentionally fire only when the identifying triple changes.
    }, [documentId, lcmsXmlGUID]);

    const isFresh = resolved !== undefined && resolved.key === requestedKey;
    const data = isFresh ? resolved!.data : undefined;
    const error = isFresh ? resolved!.error : undefined;
    // Loading until a response for the current key has settled.
    const loading = requestedKey !== undefined && !isFresh;
    const is404 = error instanceof AxiosError && error.response?.status === 404;
    // isNotTracked merges the 200-null-record and 404 paths so the panel
    // and badge hide consistently across the service deploy transition
    // (old lambda still 404s) and continue to hide once the new lambda
    // returns 200-empty everywhere. Guarded on `data !== undefined` so
    // the flag does not fire while the request is still in flight —
    // `data?.latestChangeAt == null` would otherwise be true for the
    // pre-settle `undefined` state and mislabel loading as empty.
    const isNotTracked =
        is404 || (data !== undefined && data.latestChangeAt == null);

    return { ...api, data, error, loading, is404, isNotTracked };
};

/**
 * Manual fetch helper for the pre-rendered diff of a specific topic version
 * (`GET topic-changes/{documentId}/{lcmsXmlGUID}/diff/{toVersionId}`).
 *
 * The What's-Changed panel calls `fetch(...)` on first expansion and consults
 * its React context cache (`WhatsChangedPanelContext`) before firing so that
 * expand/collapse cycles on the same mount reuse the response.
 */
export const useTopicDiff = (): TopicDiffApiType & {
    is404: boolean;
    fetch: (
        documentId: string,
        lcmsXmlGUID: string,
        toVersionId: string
    ) => Promise<void>;
} => {
    // `throwOnError: true` so makeRequest rejects on failure. Consumers
    // (WhatsChangedPanel.loadDiffForVersion) can then wrap the call in
    // try/catch to attribute an error to the specific request they just
    // fired, rather than reading the shared `api.error` state which can
    // persist across prop changes and leak a prior topic's / version's
    // failure into an unrelated panel render.
    const api = useApi({ method: 'GET' }, true) as TopicDiffApiType;

    const fetch = async (
        documentId: string,
        lcmsXmlGUID: string,
        toVersionId: string
    ) => {
        await api.makeRequest({
            url: `topic-changes/${documentId}/${lcmsXmlGUID}/diff/${toVersionId}`,
        });
    };

    const is404 =
        api.error instanceof AxiosError && api.error.response?.status === 404;

    return { ...api, is404, fetch };
};
